import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { API } from "aws-amplify";
import { Context } from "../../../../Context";

const PaymentModal = ({
  selectedTable,
  institutionData,
  onClose,
  saveKOT,
  setKOTByTable,
  setTables,
  setCurrentKOT,
  setCustomerDetails,
  fetchTableData,
  fetchPendingOrders,
  setTableOccupancy,
  updateTableOccupancy,
  localBillItems,
  orderId,
  customerDetails,
}) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const paymentMethods = ["QR", "Cash", "Card"];
  const { setLoader, hotelBookings, addHotelService } = useContext(Context);
  const tableIdStr = String(selectedTable?.id || "");
  const [booking, setBooking] = useState(null);
  const [extractedOrderIds, setExtractedOrderIds] = useState([]);

  useEffect(() => {
    if (!selectedTable || !orderId) {
      console.log("Missing selectedTable or orderId");
      return;
    }

    if (typeof orderId === "string" || typeof orderId === "number") {
      setExtractedOrderIds([orderId]);
      console.log("Direct order ID:", orderId);
      return;
    }

    if (Array.isArray(orderId)) {
      setExtractedOrderIds(orderId);
      console.log("Array of order IDs:", orderId);
      return;
    }

    if (typeof orderId === "object" && orderId !== null) {
      if (orderId.orders && typeof orderId.orders === "object") {
        const orderIdsForTable = Object.keys(orderId.orders);
        setExtractedOrderIds(orderIdsForTable);
        console.log("Extracted Order IDs from object:", orderIdsForTable);
      } else {
        const keys = Object.keys(orderId);
        setExtractedOrderIds(keys);
        console.log("Object keys as order IDs:", keys);
      }
    }
  }, [selectedTable, orderId]);

  const saveBill = async () => {
    setLoader(true);
    try {
      await API.put(
        "main",
        `/admin/orders-items/${institutionData.institution}/${tableIdStr}`,
        {
          body: {
            items: localBillItems,
            serviceTaxPercentage:
              institutionData?.serviceTax !== undefined
                ? institutionData.serviceTax
                : 0,
            cgstPercentage:
              institutionData?.cgst !== undefined ? institutionData.cgst : 2.5,
            sgstPercentage:
              institutionData?.sgst !== undefined ? institutionData.sgst : 2.5,
          },
        }
      );

      await API.post(
        "main",
        `/admin/orders-bill/${institutionData.institution}/${tableIdStr}`,
        {
          body: {
            items: localBillItems,
          },
        }
      );
      //  setLoader(false)
    } catch (error) {
      console.error("Error saving KOT:", error);
      toast.error("Failed to save KOT. Please try again.");
      setLoader(false);
    }
  };

  const handlePayment = async () => {
    setLoader(true);

    if (!selectedPaymentMethod) {
      toast.error("Please select a payment method.");
      setLoader(false);
      return;
    }

    try {
      await saveBill();

      // Create bill link with extracted order IDs
      let billLink = "";
      if (extractedOrderIds.length > 0) {
        const firstOrderId = extractedOrderIds[0].split(",")[0];
        billLink = `https://bhukkadrestrocafe.awsaiapp.com/receipt?orderIds=${firstOrderId}`;
      }

      // Only send message if phone number exists and we have a bill link
      const customerPhone = customerDetails[selectedTable?.id]?.phone;
      if (
        customerPhone &&
        billLink &&
        institutionData.institution === "bhuk2323"
      ) {
        try {
          await API.post(
            "main",
            `/admin/send-message/${institutionData.institution}`,
            {
              body: {
                customerName: customerDetails[selectedTable?.id]?.name || "",
                customerPhone: `+91${customerPhone}`,
                billLink: billLink,
                companyName: institutionData.companyName,
              },
            }
          );
        } catch (msgError) {
          console.error("Error sending message:", msgError);
          setLoader(false);
        }
      }

      await API.post(
        "main",
        `/admin/settle-bill/${institutionData.institution}/${tableIdStr}`,
        {
          body: {
            paymentStatus: true,
            paymentMode: "offline",
            paymentMethod: selectedPaymentMethod,
            status: "delivered",
          },
        }
      );

      //save-bill-to-hotel

      if (selectedPaymentMethod === "ROOM") {
        let totalFoodBill = localBillItems.reduce((total, bill) => {
          return total + bill.price * bill.quantity;
        }, 0);
        await addHotelService(booking.bookingId, {
          [new Date().toISOString().split("T")[0]]: [
            {
              food_charges: totalFoodBill,
              extra_bed_charges: 0,
              laundry_charges: 0,
              manual_charges: 0,
              minibar_charges: 0,
            },
          ],
        });
      }

      onClose();
      setLoader(false);

      // Clean up local storage
      let kotData = JSON.parse(localStorage.getItem("kotOrders") || "{}");
      if (kotData && kotData.kotTables) {
        delete kotData.kotTables[tableIdStr];
        localStorage.setItem("kotOrders", JSON.stringify(kotData));
      }

      await fetchPendingOrders();

      setKOTByTable((prev) => ({
        ...prev,
        [tableIdStr]: null,
      }));

      setCustomerDetails((prev) => ({
        ...prev,
        [tableIdStr]: { phone: null, name: null },
      }));

      toast.success("Payment Successful!");
    } catch (error) {
      console.error("Payment Error:", error);
      toast.error("Failed to process payment.");
      setLoader(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-96">
        <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">
          Settle Bill
        </h2>
        <p className="text-gray-600 mb-4">
          Table: <strong>{selectedTable?.id}</strong>
        </p>

        <h3 className="font-semibold mb-3 text-gray-700">
          Choose Payment Method:
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {paymentMethods.map((method) => (
            <button
              key={method}
              className={`p-3 rounded-md font-semibold border ${
                selectedPaymentMethod === method
                  ? "bg-green-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setSelectedPaymentMethod(method)}
            >
              {method}
            </button>
          ))}

          {institutionData.integration === "HOTEL" && (
            <button
              className={`p-3 rounded-md font-semibold border ${
                selectedPaymentMethod === "ROOM"
                  ? "bg-green-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setSelectedPaymentMethod("ROOM")}
            >
              HOTEL BILL
            </button>
          )}
        </div>

        {selectedPaymentMethod === "ROOM" && (
          <div className="flex flex-row gap-2 my-2">
            <h3>Select Room:</h3>
            <select
              className="border rounded-md p-2 w-full"
              value={booking?.bookingId || ""}
              onChange={(e) =>
                setBooking(
                  hotelBookings.find((b) => b.bookingId === e.target.value)
                )
              }
            >
              <option value="" disabled>
                Select a room
              </option>
              {hotelBookings.map((booking) => (
                <option key={booking.bookingId} value={booking.bookingId}>
                  {booking.roomNumber}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedPaymentMethod === "ROOM" && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-3 py-2 rounded-md mt-3 text-sm">
            <strong>Note:</strong> Payment will be collected during hotel checkout.
          </div>
        )}

        <div className="flex justify-between mt-6">
          <button
            onClick={onClose}
            className="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={handlePayment}
            className={`px-4 py-2 rounded-md text-white bg-green-700`}
          >
            Confirm Payment
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
